import React, { FC, useEffect, useState, useRef } from 'react'
import styles from './UserArtworks.module.scss'
import ArtistInfo from '../components/Header/ArtistInfo'
import { getHrefFunction } from 'components/SA/SearchProvider/helpers/url'
import ArtistSeriesCarousel from '../components/ArtistSeriesCarousel'
import { slugify } from 'lib/slugify'
import useOnScreen from 'hooks/onScreen'
import { FilterButton } from 'components/SA/Filters'
import { updateClientSideUrl } from 'components/SA/Facets/helpers/url'
import { useSelector } from 'react-redux'
import useModal from 'hooks/useModal'
import FiltersChips from '../components/FiltersChips/FiltersChips'
import {
  getPaginationResultsPerPage,
  getPaginationPageNumber,
  getSearchQuery,
  getTotalResults,
  getSelectedCategoryFacet,
  getSearchProviderSort,
  getIsPrintsPage,
  getSharedFacetNames,
  getSelectedFacets,
} from 'selectors/searchProvider'
import { getPathParams, getSelectedFacetsCount } from '../selectors/app'
import { getAccountUserID } from 'apps/public/profile/artistProfile/selectors'
import {
  DEFAULT_APP,
  DEFAULT_ALL_ARTWORKS_URL,
  DEFAULT_ALL_ARTWORKS_SORT,
  ORIGINALS_ALLOWED_FACETS,
  ORIGINALS_FACETS_ORDER_AND_IS_OPEN,
} from '../helpers/constants'
import { getMeasurementUnit, getUserID } from 'selectors/user/session'
import { getUserPreferencesDestinationZone } from 'selectors/user/cookie'
import useSearchArtworks from 'components/SA/SearchProvider/hooks/useSearchArtworks'
import useGetFacets from 'components/SA/SearchProvider/hooks/useGetFacets'
import { SearchApi } from 'components/SA/SearchProvider/types'
import { ConstructorSearchService } from 'components/SA/SearchProvider/services/constructor/constructorService'
import SearchProviderPagination from 'components/SA/SearchProvider/components/ArtworkSearchPagination'
import FiltersAndArtworksGrid from 'components/SA/SearchProvider/components/FiltersAndArtworksGrid/FiltersAndArtworksGrid'
import UserPolaroidList from '../components/UserPolaroids/UserPolaroidList'
const MAXIMUM_TOTAL_RESULTS = 10000
const ALL_ARTWORKS_FILTERS_MODAL_NAME = 'allArtworksFilters'

const UserArtworks: FC = () => {
  const selectedCategoryFacet = useSelector(getSelectedCategoryFacet)
  const isPrintsPage = useSelector(getIsPrintsPage)
  const selectedFilters = useSelector(getSelectedFacets)
  const searchQuery = useSelector(getSearchQuery)
  const sort = useSelector(getSearchProviderSort)
  const page = useSelector(getPaginationPageNumber)
  const hitsPerPage = useSelector(getPaginationResultsPerPage)
  const totalResults = useSelector(getTotalResults)
  const selectedFacetsCount = useSelector(getSelectedFacetsCount)
  const destinationZone = useSelector(getUserPreferencesDestinationZone)
  const measurementUnit = useSelector(getMeasurementUnit)
  const userId = useSelector(getUserID)
  const artistId = String(useSelector(getAccountUserID))
  const searchService: SearchApi = new ConstructorSearchService({ userId: String(userId) })
  const pathParams = useSelector(getPathParams)
  const sharedFacetNames = useSelector(getSharedFacetNames)
  const predefinedFilters = { artist_id: [artistId] }
  const allArtworksUrl = `${DEFAULT_ALL_ARTWORKS_URL}/${artistId}`

  const { facetsList, isLoadingFacets } = useGetFacets({
    baseUrl: allArtworksUrl,
    nextAppRoute: DEFAULT_APP,
    searchService,
    searchQuery,
    selectedFilters,
    predefinedFilters,
    page,
    resultsPerPage: hitsPerPage,
    destinationZone,
    selectedCategory: slugify(selectedCategoryFacet),
    isPrintsPage,
    userSizePreference: measurementUnit,
    hardcodedFacets: ORIGINALS_ALLOWED_FACETS,
    hardcodedFacetsOrderAndIsOpen: ORIGINALS_FACETS_ORDER_AND_IS_OPEN,
    shouldUpdateFacetsReduxStore: true,
    defaultSortObject: DEFAULT_ALL_ARTWORKS_SORT,
  })
  const { artworksResults, isLoadingArtworks } = useSearchArtworks({
    searchService,
    searchQuery,
    selectedFilters,
    predefinedFilters,
    sort,
    page,
    resultsPerPage: hitsPerPage,
    destinationZone,
    selectedCategory: selectedCategoryFacet,
    isPrintsPage,
    userSizePreference: measurementUnit,
    defaultSortObject: DEFAULT_ALL_ARTWORKS_SORT,
  })
  const [shouldLoadComponent, setShouldLoadComponent] = useState(false)
  const [isFiltersVisible, setIsFiltersVisible] = useState(true)

  const { showModal, closeModal } = useModal()
  const elementRef = useRef(null)
  const { isViewable } = useOnScreen(elementRef)

  const handleOpenMobileFilter = () => {
    showModal(ALL_ARTWORKS_FILTERS_MODAL_NAME)
  }

  const customClearFilterCallback = () => {
    const href = getHrefFunction({
      facetsAndQueryParams: {},
      pathParams,
      sharedFacetNames,
    })()
    const newUrl = `${allArtworksUrl}${href}`
    updateClientSideUrl(newUrl, DEFAULT_APP)
  }

  useEffect(() => {
    if (isViewable) {
      setShouldLoadComponent(true)
    }
  }, [isViewable])

  useEffect(() => {
    const closeFilterOnDesktop = () => {
      if (window.innerWidth > 999) {
        closeModal()
      }
    }

    window.addEventListener('resize', closeFilterOnDesktop)
    return () => window.removeEventListener('resize', closeFilterOnDesktop)
  }, [])

  const isAtLeastOneFilterSelected =
    Object.keys(selectedFilters).length !== 0 &&
    !selectedFilters.hasOwnProperty(hitsPerPage) &&
    !selectedFilters.hasOwnProperty(page) &&
    !selectedFilters.hasOwnProperty('section') &&
    !selectedFilters.hasOwnProperty(userId)

  return (
    <div className={styles.allArtworksWrapper}>
      <div className={styles.userArtworkInfoContainer}>
        <ArtistInfo
          isFiltersVisible={isFiltersVisible}
          setIsFiltersVisible={setIsFiltersVisible}
          onOpenMobileFilter={handleOpenMobileFilter}
          customClearFilterCallback={customClearFilterCallback}
          baseUrl={allArtworksUrl}
        />
      </div>

      <div className={styles.filterButtonChipsContainer}>
        <div className={styles.desktopFilterButton}>
          <FilterButton
            onClickFilterButton={() => setIsFiltersVisible((prevValue) => !prevValue)}
            isFiltersVisible={isFiltersVisible}
            filtersSelectedCount={selectedFacetsCount}
          />
        </div>
        <FiltersChips
          customClearFilterCallback={customClearFilterCallback}
          baseUrl={allArtworksUrl}
        />
      </div>

      <FiltersAndArtworksGrid
        filterModalName={ALL_ARTWORKS_FILTERS_MODAL_NAME}
        isFiltersVisible={isFiltersVisible}
        filters={facetsList}
        isFiltersLoading={isLoadingFacets}
        baseUrl={allArtworksUrl}
        nextAppRoute={DEFAULT_APP}
        setIsFiltersVisible={setIsFiltersVisible}
        closeModal={closeModal}
        customClearFilterCallback={customClearFilterCallback}
        filteredResultsCount={totalResults}
      >
        <UserPolaroidList
          artworks={artworksResults}
          isLoading={isLoadingArtworks}
          isFiltersVisible={isFiltersVisible}
          searchQuery={searchQuery}
          isAtLeastOneFilterSelected={isAtLeastOneFilterSelected}
        />
      </FiltersAndArtworksGrid>

      <div className={styles.userArtworkPaddingContainer} ref={elementRef}>
        <SearchProviderPagination
          pageNumber={page}
          hitsPerPage={hitsPerPage}
          totalResults={totalResults > MAXIMUM_TOTAL_RESULTS ? MAXIMUM_TOTAL_RESULTS : totalResults}
          selectedCategory={selectedCategoryFacet}
          baseUrl={allArtworksUrl}
          nextAppRoute={DEFAULT_APP}
        />
        <ArtistSeriesCarousel />
      </div>
    </div>
  )
}

export default UserArtworks
